// src/hooks/useAdminChat.js

import { useState, useEffect, useRef } from 'react';
import adminApiService from '../services/adminApiService';

export function useAdminChat(sessionId) {
  const [loading, setLoading] = useState(true);
  const [metadata, setMetadata] = useState(null);
  const [turns, setTurns] = useState([]);
  const [suggestions, setSuggestions] = useState({});
  const [extractedSuggestions, setExtractedSuggestions] = useState({});
  const [selectedSuggestions, setSelectedSuggestions] = useState({});
  const [reports, setReports] = useState({});
  const [activeSuggestionTurnId, setActiveSuggestionTurnId] = useState(null);
  const [activeReportId, setActiveReportId] = useState(null);
  const [feedbackData, setFeedbackData] = useState({});
  const [suggestionsFeedback, setSuggestionsFeedback] = useState({});
  const [reportsFeedback, setReportsFeedback] = useState({});
  
  const messagesContainerRef = useRef(null);
  const rightPaneRef = useRef(null);

  useEffect(() => {
    loadConversationData();
  }, [sessionId]);

  const loadConversationData = async () => {
    try {
      setLoading(true);
      
      // Load metadata
      const metadataData = await adminApiService.getConversationMetadata(sessionId);
      setMetadata(metadataData);
      
      // Load turns
      const turnsData = await adminApiService.getConversationTurns(sessionId);
      
      // Transform server turns to match client structure
      const transformedTurns = turnsData.map(turn => ({
        id: turn.turnNumber,
        userText: turn.userMessage?.content || '',
        botFollowup: turn.assistantMessage?.content || '',
        currentFiles: turn.userMessage?.files || [],
        suggestion: turn.suggestions ? '' : undefined,
        turnNumber: turn.turnNumber,
        firestoreTurnId: turn.id
      }));
      
      setTurns(transformedTurns);
      
      // Load suggestions, reports, and feedback for each turn
      const suggestionsData = {};
      const extractedData = {};
      const selectedData = {};
      const reportsData = {};
      const feedbackMap = {};
      const suggestionsFeedbackMap = {};
      const reportsFeedbackMap = {};
      
      for (const turn of turnsData) {
        const turnId = turn.turnNumber;
        
        // Extract assistant message feedback
        if (turn.assistantMessage?.feedback) {
          feedbackMap[turnId] = {
            type: turn.assistantMessage.feedback.type,
            comment: turn.assistantMessage.feedback.comment || '',
            timestamp: turn.assistantMessage.feedback.timestamp
          };
        }
        
        // Extract suggestions and suggestions feedback
        if (turn.suggestions) {
          suggestionsData[turnId] = turn.suggestions.rawContent || '';
          extractedData[turnId] = turn.suggestions.extracted || {};
          selectedData[turnId] = turn.suggestions.selected || [];
          
          // Suggestions feedback
          if (turn.suggestions.feedback) {
            suggestionsFeedbackMap[turnId] = {
              type: turn.suggestions.feedback.type,
              comment: turn.suggestions.feedback.comment || '',
              timestamp: turn.suggestions.feedback.timestamp
            };
          }
          
          // Load reports for this turn
          const turnReports = await adminApiService.getTurnReports(sessionId, turn.id);
          
          if (turnReports.length > 0) {
            reportsData[turnId] = turnReports.map(report => ({
              version: report.version,
              content: report.content,
              selectedSuggestions: report.selectedSuggestions || [],
              isComplete: true,
              timestamp: report.timestamp,
              feedback: report.feedback || null
            }));
            
            // Extract report feedback
            turnReports.forEach(report => {
              if (report.feedback) {
                const reportKey = `${turnId}_v${report.version}`;
                reportsFeedbackMap[reportKey] = {
                  type: report.feedback.type,
                  comment: report.feedback.comment || '',
                  timestamp: report.feedback.timestamp
                };
              }
            });
          }
        }
      }
      
      setSuggestions(suggestionsData);
      setExtractedSuggestions(extractedData);
      setSelectedSuggestions(selectedData);
      setReports(reportsData);
      setFeedbackData(feedbackMap);
      setSuggestionsFeedback(suggestionsFeedbackMap);
      setReportsFeedback(reportsFeedbackMap);
      
    } catch (error) {
      console.error('Error loading conversation data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Toggle suggestions view
  const toggleSuggestion = (turnId) => {
    if (activeSuggestionTurnId === turnId && !activeReportId) {
      setActiveSuggestionTurnId(null);
    } else {
      setActiveSuggestionTurnId(turnId);
      setActiveReportId(null);
    }
  };

  // Toggle report view
  const toggleReport = (turnId, version) => {
    if (activeReportId?.turnId === turnId && activeReportId?.version === version) {
      setActiveSuggestionTurnId(null);
      setActiveReportId(null);
    } else {
      setActiveSuggestionTurnId(turnId);
      setActiveReportId({ turnId, version });
    }
  };

  // Close right pane
  const closeRightPane = () => {
    setActiveSuggestionTurnId(null);
    setActiveReportId(null);
  };

  // Get right pane content
  const getRightPaneContent = () => {
    if (!activeSuggestionTurnId) return null;
    
    if (activeReportId && activeReportId.turnId === activeSuggestionTurnId) {
      const turnReports = reports[activeReportId.turnId] || [];
      const report = turnReports.find(r => r.version === activeReportId.version);
      const reportKey = `${activeReportId.turnId}_v${activeReportId.version}`;
      
      return {
        type: 'report',
        content: report?.content || '',
        isComplete: report?.isComplete || false,
        version: activeReportId.version,
        selectedSuggestions: report?.selectedSuggestions || [],
        feedback: reportsFeedback[reportKey] || null
      };
    } else {
      return {
        type: 'suggestions',
        content: suggestions[activeSuggestionTurnId] || '',
        extracted: extractedSuggestions[activeSuggestionTurnId] || {},
        selected: selectedSuggestions[activeSuggestionTurnId] || [],
        feedback: suggestionsFeedback[activeSuggestionTurnId] || null
      };
    }
  };

  const activeTurn = activeSuggestionTurnId
    ? turns.find((t) => t.id === activeSuggestionTurnId)
    : null;
  
  const isRightPaneOpen = !!activeTurn;

  return {
    loading,
    metadata,
    turns,
    activeSuggestionTurnId,
    suggestions,
    activeTurn,
    isRightPaneOpen,
    messagesContainerRef,
    rightPaneRef,
    toggleSuggestion,
    toggleReport,
    closeRightPane,
    extractedSuggestions,
    selectedSuggestions,
    getRightPaneContent,
    reports,
    activeReportId,
    feedbackData,
    suggestionsFeedback,
    reportsFeedback
  };
}
